/**
 * Domain templates for Mesa Vision C (Opción A — same skeleton, four domains).
 */

import type { DomainTemplateDef, DomainTemplateId } from "@/lib/studyPackageTypes";

export const DOMAIN_TEMPLATES: Record<DomainTemplateId, DomainTemplateDef> = {
  propiedad: {
    id: "propiedad",
    labelEs: "Propiedad (casa / local)",
    labelEn: "Property (home / shop)",
    requiredViewKinds: ["planta", "corte"],
    defaultPartRoles: ["espacio", "vano", "estructura"],
    questions: [
      {
        id: "prop_program",
        labelEs: "¿Qué espacios necesita el programa? (ej. 2 dorms, taller, terraza)",
        labelEn: "What spaces does the program need? (e.g. 2 beds, workshop, terrace)",
        required: true,
        placeholderEs: "Núcleo habitable, taller maker, terraza cubierta…",
        placeholderEn: "Living core, maker workshop, covered terrace…",
      },
      {
        id: "prop_levels",
        labelEs: "¿Cuántas plantas / niveles?",
        labelEn: "How many floors / levels?",
        required: true,
        placeholderEs: "1 o 2",
        placeholderEn: "1 or 2",
      },
      {
        id: "prop_area",
        labelEs: "¿Área aproximada (pies² o m²)?",
        labelEn: "Approx. area (sq ft or m²)?",
        required: true,
        placeholderEs: "ej. 1800 pies²",
        placeholderEn: "e.g. 1800 sq ft",
      },
      {
        id: "prop_resilience",
        labelEs: "Prioridad PR: huracán Cat 5 / salitre / ambos?",
        labelEn: "PR priority: Cat-5 wind / salt air / both?",
        required: true,
        placeholderEs: "Ambos — bloque 8\" + hardware inox",
        placeholderEn: "Both — 8\" block + stainless hardware",
      },
      {
        id: "prop_site",
        labelEs: "¿Hay solar ya, o es concepto sin terreno?",
        labelEn: "Do you have a lot yet, or concept without land?",
        required: false,
        placeholderEs: "Sin solar — solo idea",
        placeholderEn: "No lot — concept only",
      },
    ],
  },
  producto: {
    id: "producto",
    labelEs: "Producto / carcasa",
    labelEn: "Product / enclosure",
    requiredViewKinds: ["ortho", "despiece"],
    defaultPartRoles: ["pieza", "cara", "fijacion"],
    questions: [
      {
        id: "prod_function",
        labelEs: "¿Qué hace el producto y para quién?",
        labelEn: "What does the product do and for whom?",
        required: true,
      },
      {
        id: "prod_envelope",
        labelEs: "¿Envolvente / tamaño máximo (mm)?",
        labelEn: "Envelope / max size (mm)?",
        required: true,
        placeholderEs: "L×W×H",
        placeholderEn: "L×W×H",
      },
      {
        id: "prod_material",
        labelEs: "¿Material preferido (PETG, ASA, Al 6061…)?",
        labelEn: "Preferred material (PETG, ASA, Al 6061…)?",
        required: true,
      },
      {
        id: "prod_ip",
        labelEs: "¿IP / sellado / uso exterior PR?",
        labelEn: "IP rating / sealing / outdoor PR use?",
        required: false,
      },
    ],
  },
  mecanismo: {
    id: "mecanismo",
    labelEs: "Mecanismo",
    labelEn: "Mechanism",
    requiredViewKinds: ["ensamble", "explosion"],
    defaultPartRoles: ["eslabon", "union", "actuador"],
    questions: [
      {
        id: "mech_dof",
        labelEs: "¿Qué movimiento debe lograr (DoF / trayectoria)?",
        labelEn: "What motion should it achieve (DoF / path)?",
        required: true,
      },
      {
        id: "mech_load",
        labelEs: "¿Carga o fuerza esperada?",
        labelEn: "Expected load or force?",
        required: true,
      },
      {
        id: "mech_drive",
        labelEs: "¿Actuación (manual, NEMA, servo…)?",
        labelEn: "Actuation (manual, NEMA, servo…)?",
        required: true,
      },
      {
        id: "mech_env",
        labelEs: "¿Ambiente (taller seco, costa, polvo)?",
        labelEn: "Environment (dry shop, coast, dust)?",
        required: false,
      },
    ],
  },
  pinout: {
    id: "pinout",
    labelEs: "Pinout / circuito",
    labelEn: "Pinout / circuit",
    requiredViewKinds: ["diagrama", "pinout_iso"],
    defaultPartRoles: ["pin", "net", "cable"],
    questions: [
      {
        id: "pin_mcu",
        labelEs: "¿MCU / placa (ESP32, Pico, Arduino…)?",
        labelEn: "MCU / board (ESP32, Pico, Arduino…)?",
        required: true,
      },
      {
        id: "pin_peripherals",
        labelEs: "¿Periféricos a cablear?",
        labelEn: "Peripherals to wire?",
        required: true,
        placeholderEs: "OLED I2C, relay, sensor…",
        placeholderEn: "OLED I2C, relay, sensor…",
      },
      {
        id: "pin_power",
        labelEs: "¿Alimentación (5V Buck, USB, 12V…)?",
        labelEn: "Power (5V Buck, USB, 12V…)?",
        required: true,
      },
      {
        id: "pin_constraints",
        labelEs: "¿Restricciones (no 12V a GPIO, GND común…)?",
        labelEn: "Constraints (no 12V on GPIO, common GND…)?",
        required: false,
      },
    ],
  },
};

export function getDomainTemplate(id: DomainTemplateId): DomainTemplateDef {
  return DOMAIN_TEMPLATES[id];
}

export function listDomainTemplates(): DomainTemplateDef[] {
  return (Object.keys(DOMAIN_TEMPLATES) as DomainTemplateId[]).map((id) => DOMAIN_TEMPLATES[id]);
}

export function requiredQuestionIds(templateId: DomainTemplateId): string[] {
  return DOMAIN_TEMPLATES[templateId].questions.filter((q) => q.required).map((q) => q.id);
}

export function isBriefComplete(
  templateId: DomainTemplateId,
  answers: Record<string, { value: string }>
): boolean {
  return requiredQuestionIds(templateId).every((id) => {
    const v = answers[id]?.value;
    return typeof v === "string" && v.trim().length > 0;
  });
}
